/**
 * What the agent is doing to this page, right now.
 *
 * Three surfaces need the same answer and must never disagree: the intro label
 * that names the agent-touchable components, the Emjae bubble that narrates
 * what is being touched, and the dock icon that may only animate during real
 * work. A single store means the icon cannot be "busy" while the bubble says
 * idle, which is the failure mode that makes an activity indicator worthless.
 *
 * `idle` is a real state, not the absence of one. An indicator that pulses on a
 * timer teaches people to ignore it, so nothing here animates unless a tool
 * actually started.
 */
import { create } from 'zustand';
import { resolveModelContext } from './modelContext';

/** Where the agent is in one tool call. */
export type AgentPhase = 'idle' | 'thinking' | 'acting' | 'settled' | 'refused';

/**
 * A component an agent can drive on this surface. Registered by the view that
 * owns it, so the intro label lists what is REALLY wired rather than a
 * hand-maintained copy that drifts the first time a tool is renamed.
 */
export interface AgentTouchable {
  /** Stable key, also the `data-agent-target` value on the DOM node. */
  readonly key: string;
  /** What an operator calls it. */
  readonly label: string;
  /** One clause: what an agent can do to it. */
  readonly can: string;
}

interface AgentActivityState {
  readonly phase: AgentPhase;
  /** Registered tool name, e.g. `planner_approve_keywords`. */
  readonly tool: string | null;
  /** Comic-panel line the bubble narrates. Short by construction. */
  readonly line: string | null;
  /** Component keys this call is touching, for the highlight. */
  readonly targets: readonly string[];
  /** Surface inventory, keyed so a re-register replaces rather than duplicates. */
  readonly touchables: Readonly<Record<string, AgentTouchable>>;
  /** Bumped on every settle so the dock can fire a one-shot, not a loop. */
  readonly dinkNonce: number;

  registerTouchables(items: readonly AgentTouchable[]): void;
  clearTouchables(keys: readonly string[]): void;
  begin(tool: string, line: string, targets?: readonly string[]): void;
  narrate(line: string): void;
  act(line: string): void;
  settle(line: string): void;
  refuse(line: string): void;
  reset(): void;
}

/**
 * Whether this document has the WebMCP surface at all.
 *
 * The same feature test `mountOperatorSurface` early-returns on.
 */
export function hasAgentSurface(): boolean {
  return Boolean(resolveModelContext());
}

const IDLE = {
  phase: 'idle' as const,
  tool: null,
  line: null,
  targets: [] as readonly string[],
};

export const useAgentActivity = create<AgentActivityState>((set) => ({
  ...IDLE,
  touchables: {},
  dinkNonce: 0,

  registerTouchables(items) {
    // Gated at the source rather than at each call site, because a view that
    // owns a touchable has no reason to know whether the browser ships WebMCP,
    // and one call site forgetting the check is what put the intro label in
    // front of every Firefox and Safari operator.
    if (!hasAgentSurface()) return;
    set((s) => {
      const next = { ...s.touchables };
      for (const item of items) next[item.key] = item;
      return { touchables: next };
    });
  },

  clearTouchables(keys) {
    set((s) => {
      const next = { ...s.touchables };
      for (const key of keys) delete next[key];
      return { touchables: next };
    });
  },

  begin(tool, line, targets = []) {
    set({ phase: 'thinking', tool, line, targets });
  },

  narrate(line) {
    set((s) => (s.phase === 'idle' ? s : { ...s, line }));
  },

  act(line) {
    set((s) => (s.phase === 'idle' ? s : { ...s, phase: 'acting', line }));
  },

  /** Terminal success. Bumps the nonce so the dock dinks exactly once. */
  settle(line) {
    set((s) => ({ ...s, phase: 'settled', line, dinkNonce: s.dinkNonce + 1 }));
  },

  refuse(line) {
    set((s) => ({ ...s, phase: 'refused', line }));
  },

  reset() {
    set(IDLE);
  },
}));

/** True only while a tool call is genuinely in flight. Never a timer. */
export function isAgentWorking(phase: AgentPhase): boolean {
  return phase === 'thinking' || phase === 'acting';
}

/**
 * Bridges Chrome's own `toolactivated` event into the store, so the surfaces
 * light up even for tools this app did not write the narration for. Chrome
 * Returns a disposer. Safe to call when WebMCP is absent: it feature-detects
 * and does nothing, per the standing rule that there is no kill switch and no
 * polyfill.
 */
export function bridgeChromeToolEvents(): () => void {
  const context = resolveModelContext();
  if (!context || typeof context.addEventListener !== 'function') return () => {};

  const onActivated = (event: Event) => {
    const name =
      (event as unknown as { tool?: { name?: string }; name?: string }).tool?.name ??
      (event as unknown as { name?: string }).name ??
      'a tool';
    useAgentActivity.getState().begin(name, `Routing ${name}…`);
  };

  context.addEventListener('toolactivated', onActivated);
  return () => context.removeEventListener('toolactivated', onActivated);
}
